import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";

const chartWidth = 880;
const chartHeight = 220;
const chartPadding = { top: 22, right: 24, bottom: 40, left: 80 };
const rangeOptions = [
  { value: "all", label: "All", periodCount: null },
  { value: "sixMonths", label: "Last 6 months", periodCount: 6 },
  { value: "year", label: "Last year", periodCount: 12 },
];

const getYAxisStep = (maxValue) => {
  if (maxValue <= 10000) return 1000;
  if (maxValue <= 50000) return 5000;
  if (maxValue <= 100000) return 10000;
  if (maxValue <= 500000) return 50000;
  return 100000;
};

const formatAxisValue = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const ReportsIncomeTrends = () => {
  const [report, setReport] = useState({ Periods: [], Categories: [] });
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rangeScale, setRangeScale] = useState("all");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const [reportRes, currenciesRes] = await Promise.all([
          apiClient.get("/reports/income-trends"),
          apiClient.get("/currencies"),
        ]);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setReport({
          Periods: Array.isArray(reportRes.data?.Periods) ? reportRes.data.Periods : [],
          Categories: Array.isArray(reportRes.data?.Categories) ? reportRes.data.Categories : [],
        });
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load income trends report.");
        setReport({ Periods: [], Categories: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const visiblePeriods = useMemo(() => {
    const periods = report.Periods || [];
    const selectedOption = rangeOptions.find((option) => option.value === rangeScale);
    return selectedOption?.periodCount ? periods.slice(-selectedOption.periodCount) : periods;
  }, [report, rangeScale]);

  const chartCards = useMemo(() => {
    const visiblePeriodIds = new Set(visiblePeriods.map((period) => Number(period.PeriodId)));
    return (report.Categories || []).map((category) => {
      const points = Array.isArray(category.Points)
        ? category.Points.filter((point) => visiblePeriodIds.has(Number(point.PeriodId)))
        : [];
      const rawMaxValue = points.reduce((max, point) => Math.max(max, Number(point.Income || 0)), 0);
      const yAxisStep = getYAxisStep(rawMaxValue);
      const maxValue = rawMaxValue > 0 ? Math.ceil(rawMaxValue / yAxisStep) * yAxisStep : yAxisStep;
      const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
      const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;
      const barWidth = points.length > 0 ? Math.max(12, Math.min(36, innerWidth / Math.max(points.length * 1.8, 1))) : 12;
      const gap = points.length > 1 ? (innerWidth - barWidth * points.length) / (points.length - 1) : 0;
      const scaleY = maxValue > 0 ? innerHeight / maxValue : 0;
      const averageValue = points.length > 0
        ? points.reduce((sum, point) => sum + Number(point.Income || 0), 0) / points.length
        : 0;
      const averageY = chartHeight - chartPadding.bottom - averageValue * scaleY;
      const bars = points.map((point, index) => {
        const value = Number(point.Income || 0);
        const height = value * scaleY;
        const x = chartPadding.left + index * (barWidth + Math.max(gap, 0));
        const y = chartHeight - chartPadding.bottom - height;
        return {
          x,
          y,
          width: barWidth,
          height,
          value,
          label: point.Description || `Period ${point.PeriodId}`,
        };
      });
      const tickValues = [];
      for (let tick = maxValue; tick >= 0; tick -= yAxisStep) {
        tickValues.push(tick);
      }

      return {
        ...category,
        maxValue,
        innerHeight,
        averageValue,
        averageY,
        tickValues,
        bars,
      };
    });
  }, [report, visiblePeriods]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Income Trends</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Income Trends
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading income trends...</p>}

      {!loading && (
        <section className="resource-list">
          {chartCards.length === 0 ? (
            <div className="panel">
              <p className="notice notice-muted">No income category data found.</p>
            </div>
          ) : (
            <>
              <section className="panel list-filter-panel">
                <div className="chart-toolbar chart-toolbar-split">
                  <div className="resource-meta">
                    {visiblePeriods[0]?.Description || "-"} to {visiblePeriods[visiblePeriods.length - 1]?.Description || "-"}
                  </div>
                  <div className="segmented-control" aria-label="Income trends period range">
                    {rangeOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`segmented-control-button${rangeScale === option.value ? " is-active" : ""}`}
                        onClick={() => setRangeScale(option.value)}
                        aria-pressed={rangeScale === option.value}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <p className="notice notice-muted report-context-note">
                Income is shown by income category over time in the default currency: {defaultCurrency || "Default"}.
              </p>

              {chartCards.map((category) => (
                <article key={category.CategoryId} className="panel">
                  <div className="section-heading">
                    <div>
                      <h2>{category.Category}</h2>
                      <p className="section-copy">
                        {category.bars.length} periods shown
                      </p>
                    </div>
                  </div>

                  <div className="table-scroll">
                    <svg
                      className="line-chart"
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      role="img"
                      aria-label={`Bar chart showing income over time for category ${category.Category}`}
                    >
                      {category.tickValues.map((tick) => {
                        const y = chartHeight - chartPadding.bottom - (category.maxValue > 0 ? (tick / category.maxValue) * category.innerHeight : 0);
                        return (
                          <g key={`${category.CategoryId}-${tick}`}>
                            <line className="line-chart-grid" x1={chartPadding.left} y1={y} x2={chartWidth - chartPadding.right} y2={y} />
                            <text className="line-chart-axis-text" x={chartPadding.left - 10} y={y + 4} textAnchor="end">
                              {formatAxisValue(tick)}
                            </text>
                          </g>
                        );
                      })}

                      <line className="line-chart-axis" x1={chartPadding.left} y1={chartPadding.top} x2={chartPadding.left} y2={chartHeight - chartPadding.bottom} />
                      <line className="line-chart-axis" x1={chartPadding.left} y1={chartHeight - chartPadding.bottom} x2={chartWidth - chartPadding.right} y2={chartHeight - chartPadding.bottom} />
                      {category.bars.length > 0 ? (
                        <line
                          className="income-trends-average-line"
                          x1={chartPadding.left}
                          y1={category.averageY}
                          x2={chartWidth - chartPadding.right}
                          y2={category.averageY}
                        />
                      ) : null}

                      {category.bars.map((bar) => (
                        <rect
                          key={`${category.CategoryId}-${bar.label}`}
                          className="income-trends-bar"
                          x={bar.x}
                          y={bar.y}
                          width={bar.width}
                          height={bar.height}
                          rx="4"
                        />
                      ))}

                      {category.bars.map((bar, index) => {
                        const showLabel =
                          index === 0 ||
                          index === category.bars.length - 1 ||
                          index % 5 === 0;
                        if (!showLabel) return null;
                        return (
                          <text
                            key={`${category.CategoryId}-label-${bar.label}`}
                            className="line-chart-axis-text"
                            x={bar.x + bar.width / 2}
                            y={chartHeight - 14}
                            textAnchor="middle"
                          >
                            {bar.label}
                          </text>
                        );
                      })}
                    </svg>
                  </div>
                </article>
              ))}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsIncomeTrends;
